// Employee relations (Phase 5c) — role-adaptive. Employees raise + track
// confidential cases; HR triages and resolves them.
import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControlLabel, Checkbox } from '@mui/material';
import { Plus, ShieldAlert } from 'lucide-react';
import { Card, SectionTitle, CenterSpinner, PrimaryBtn, OutlineBtn, Pill } from './ui';
import { EmptyState } from './components';
import { AG } from './theme/tokens';
import { api } from './api';
import { useAuth } from './auth';

const TYPES = ['Grievance', 'Harassment', 'Whistleblow', 'Safety', 'Other'];
const NEXT = { Open: ['UnderReview', 'Resolved', 'Closed'], UnderReview: ['Resolved', 'Closed'], Resolved: ['Closed'], Closed: [] };
const tone = (s) => (s === 'Resolved' ? 'success' : s === 'UnderReview' ? 'info' : s === 'Closed' ? 'neutral' : 'warning');

export default function Cases({ showToast }) {
  const { isHR } = useAuth();
  return isHR ? <CasesHR showToast={showToast} /> : <CasesEmployee showToast={showToast} />;
}

function CasesEmployee({ showToast }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.myCases().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const submit = async () => {
    if (!form.subject.trim() || !form.details.trim() || busy) return; setBusy(true);
    try { await api.raiseCase(form); showToast?.('Case submitted'); setForm(null); load(); }
    catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  if (!rows) return <CenterSpinner />;
  return (
    <div className="space-y-4 fade-up">
      <div className="flex items-center justify-between" style={{ gap: 12, flexWrap: 'wrap' }}>
        <SectionTitle title="Employee Relations" sub="Raise a concern — confidentially if you prefer" />
        <PrimaryBtn onClick={() => setForm({ type: 'Grievance', subject: '', details: '', anonymous: false })}><Plus size={14} style={{ marginRight: 6 }} /> Raise a concern</PrimaryBtn>
      </div>
      {rows.length === 0 ? <Card><EmptyState icon={ShieldAlert} title="No cases" desc="Anything you raise is handled confidentially by HR." /></Card> : (
        <div style={{ display: 'grid', gap: 8 }}>
          {rows.map((c) => (
            <Card key={c.id} style={{ padding: 14 }}>
              <div className="flex items-center justify-between">
                <div>
                  <div style={{ fontWeight: 600, color: AG.text }}>{c.subject} <span style={{ color: AG.n400, fontWeight: 400 }}>· {c.type}{c.anonymous ? ' · anonymous' : ''}</span></div>
                  <div style={{ fontSize: 12, color: AG.n500 }}>{c.createdAt?.slice(0, 10)}</div>
                </div>
                <Pill tone={tone(c.status)} dot>{c.status}</Pill>
              </div>
              {c.resolution && <div style={{ fontSize: 13, color: AG.n600, marginTop: 8, borderTop: `1px solid ${AG.divider}`, paddingTop: 8 }}><b style={{ color: AG.n600 }}>Resolution:</b> {c.resolution}</div>}
            </Card>
          ))}
        </div>
      )}
      <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Raise a concern</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Type</div>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="ag-input" style={{ marginTop: 4, marginBottom: 12 }}>{TYPES.map((t) => <option key={t}>{t}</option>)}</select>
              <TextField fullWidth size="small" label="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} sx={{ mb: 1.5 }} />
              <TextField fullWidth size="small" label="Details" multiline minRows={3} value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} />
              <FormControlLabel control={<Checkbox checked={form.anonymous} onChange={(e) => setForm({ ...form, anonymous: e.target.checked })} />} label="Submit anonymously (hide my name from HR)" />
            </div>
          )}
        </DialogContent>
        <DialogActions><OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn><PrimaryBtn onClick={submit} disabled={busy}>Submit</PrimaryBtn></DialogActions>
      </Dialog>
    </div>
  );
}

function CasesHR({ showToast }) {
  const [rows, setRows] = useState(null);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.casesAll().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const apply = async (patch) => {
    if (busy) return; setBusy(true);
    try { await api.updateCase(active.id, patch); showToast?.('Case updated'); setActive(null); load(); }
    catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  if (!rows) return <CenterSpinner />;
  return (
    <div className="space-y-4 fade-up">
      <SectionTitle title="Employee Relations" sub="Triage and resolve confidential cases" />
      {rows.length === 0 ? <Card><EmptyState icon={ShieldAlert} title="No cases" desc="Cases raised by employees appear here." /></Card> : (
        <div style={{ display: 'grid', gap: 8 }}>
          {rows.map((c) => (
            <Card key={c.id} style={{ padding: 14, cursor: 'pointer' }} onClick={() => setActive({ ...c, resolution: c.resolution || '' })}>
              <div className="flex items-center justify-between">
                <div>
                  <div style={{ fontWeight: 600, color: AG.text }}>{c.subject} <span style={{ color: AG.n400, fontWeight: 400 }}>· {c.type}</span></div>
                  <div style={{ fontSize: 12, color: AG.n500 }}>{c.anonymous ? 'Anonymous' : c.raiserName} · {c.createdAt?.slice(0, 10)}</div>
                </div>
                <Pill tone={tone(c.status)} dot>{c.status}</Pill>
              </div>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={!!active} onClose={() => !busy && setActive(null)} maxWidth="sm" fullWidth>
        {active && (
          <>
            <DialogTitle>{active.subject}</DialogTitle>
            <DialogContent>
              <div style={{ fontSize: 12, color: AG.n500, marginBottom: 8 }}>{active.type} · {active.anonymous ? 'Anonymous' : active.raiserName}</div>
              <div style={{ fontSize: 14, color: AG.text, whiteSpace: 'pre-wrap', marginBottom: 16 }}>{active.details}</div>
              <TextField fullWidth size="small" label="Resolution notes" multiline minRows={2} value={active.resolution} onChange={(e) => setActive({ ...active, resolution: e.target.value })} sx={{ mb: 1 }} />
            </DialogContent>
            <DialogActions style={{ flexWrap: 'wrap', gap: 6 }}>
              <OutlineBtn onClick={() => apply({ resolution: active.resolution })} disabled={busy}>Save notes</OutlineBtn>
              {(NEXT[active.status] || []).map((s) => (
                <PrimaryBtn key={s} onClick={() => apply({ status: s, resolution: active.resolution })} disabled={busy}>Mark {s}</PrimaryBtn>
              ))}
            </DialogActions>
          </>
        )}
      </Dialog>
    </div>
  );
}
